import { useLearnerList } from "@/api/services/main/learner";
import { InnerBox } from "@/components/elements/InnerBox";
import { Pagination } from "@/components/elements/Pagination";
import { usePagination } from "@/hooks/usePagination";
import { AppRoutes } from "@/routes/config";
import { black, divider } from "@/theme/colors";
import { Stack, Typography } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LEARNER_LIST_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { LearnerListTable } from "@/features/learner/components/learner-list/LearnerListTable";
import { LearnerListTop } from "@/features/learner/components/learner-list/LearnerListTop";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { USER_TYPE } from "@/types/enum";
import { TopPage } from "@/components/elements/TopPage";

type TLearnerFilterForm = {
  keyword: string;
  industryId: string;
  planStatus: string;
  isArchive: boolean;
};

const defaultValues: TLearnerFilterForm = {
  keyword: "",
  industryId: "",
  planStatus: "",
  isArchive: false,
};

export const LearnerListPage: FC = () => {
  const pagReq = usePagination();
  const navigate = useNavigate();
  const { userType, user } = useInitSetup();

  const [formValues, setFormValues] = useState<TLearnerFilterForm>({ ...defaultValues });
  const [filters, setFilters] = useState<TLearnerFilterForm>({ ...defaultValues });
  const [_searchParams] = useSearchParams();

  const currentPage = pagReq.page || START_PAGE;
  const perPage = LEARNER_LIST_PER_PAGE;

  // Filter by companyId for support_user (show learners whose rsoCompany matches)
  const rsoCompanyId = userType === USER_TYPE.SUPPORT_USER ? user?.companyId : undefined;

  // Use API to fetch learner list
  const { data: learnerListData, isLoading } = useLearnerList({
    page: currentPage,
    perPage: perPage,
    sorts: pagReq.sorts,
    keyword: filters.keyword || undefined,
    industryId: filters.industryId || undefined,
    planStatus: filters.planStatus || undefined,
    isArchive: filters.isArchive ?? false,
    rsoCompanyId,
  });

  const learnerList = learnerListData?.data ?? [];
  const pagination = learnerListData?.pagination ?? {
    page: currentPage,
    perPage: perPage,
    total: 0,
    totalPage: 0,
    nextPage: null,
    prevPage: null,
    count: 0,
  };

  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }

    const allEmpty = !formValues.industryId && !formValues.keyword && !formValues.planStatus && !formValues.isArchive;

    if (allEmpty) {
      setFilters(defaultValues);
    }
  }, [formValues]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      isArchive: formValues.isArchive,
    }));
    navigateFirstPage();
  }, [formValues.isArchive]);

  const navigateFirstPage = () => {
    const params = new URLSearchParams(location.search);
    params.delete("page");
    navigate(`${AppRoutes.learnerList}?${params.toString()}`);
  };

  const onSubmit = (vals: TLearnerFilterForm) => {
    setFilters(vals);
    navigateFirstPage();
  };

  const handleUpdateFormValues = (key: keyof TLearnerFilterForm, value: unknown) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleClearFilter = () => {
    setFormValues(defaultValues);
    setFilters(defaultValues);
    navigateFirstPage();
  };

  return (
    <Stack minHeight="100%">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <TopPage title="外国人一覧" enablePageBack={false} />
      </Stack>

      <InnerBox padding={3} mt={3} showDivider={false} bordered={true} borderColor={divider.high}>
        <Stack>
          {/* Search and Filter Section */}
          <LearnerListTop
            formValues={formValues}
            onChangeFormValues={handleUpdateFormValues}
            onSubmit={(vals) => onSubmit(vals)}
            handleClearFilter={handleClearFilter}
            pagination={pagination}
          />
          <Stack spacing={3}>
            <LearnerListTable rows={learnerList} isLoading={isLoading} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Pagination totalPage={pagination.totalPage} />
              {learnerListData?.currentLearnerCount != null && (
                <Stack alignItems="center">
                  <Typography variant="cap12Bold" color={black[70]}>
                    有効アカウント数
                  </Typography>
                  <Typography variant="body14Semi" color={black[70]}>
                    {learnerListData.currentLearnerCount}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
      </InnerBox>
    </Stack>
  );
};
